// Manage time off balance state and calculations
import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store/store'
import {
  Balance,
  HoursValues,
  DeductedBalances,
  transformTimeOffBalanceToBalances,
  calculateDeductedBalances,
  isBirthdayAvailable,
  hasBirthdayHours,
  isBirthdayUnavailable,
} from '../utils/balanceUtils'

interface UseTimeOffBalanceReturn {
  timeOffBalanceData: any
  resultingBalances: Balance[]
  deductedBalances: DeductedBalances
  hoursValues: HoursValues
  setHoursValues: (values: HoursValues) => void
  updateHours: (id: string, value: string) => void
  resetHours: () => void
  isLoading: boolean
  hasError: boolean
  isBirthdayEnabled: boolean
  hasBirthdayBalance: boolean
  isBirthdayTaken: boolean
}

const DEFAULT_HOURS: HoursValues = {
  vacation: '0',
  float: '0',
  flexible: '0',
  other: '0',
}

export const useTimeOffBalance = (): UseTimeOffBalanceReturn => {
  const timeOffBalanceData: any = useSelector(
    (state: RootState) => state.employeeInfo.timeOffBalance.data
  )
  const isLoading = useSelector(
    (state: RootState) => state.employeeInfo.timeOffBalance.loading
  )
  const hasError = useSelector(
    (state: RootState) => !!state.employeeInfo.timeOffBalance.error
  )

  const [hoursValues, setHoursValues] = useState<HoursValues>(DEFAULT_HOURS)

  const resultingBalances = useMemo<Balance[]>(
    () => transformTimeOffBalanceToBalances(timeOffBalanceData),
    [timeOffBalanceData]
  )

  const deductedBalances = useMemo<DeductedBalances>(
    () => calculateDeductedBalances(resultingBalances, hoursValues),
    [resultingBalances, hoursValues]
  )

  const updateHours = (id: string, value: string) => {
    const numRe = /^\d*(?:\.\d{0,2})?/
    const execRes = numRe.exec(String(value || ''))
    const sanitized = execRes ? execRes[0] : ''

    setHoursValues((prev) => ({
      ...prev,
      [id]: sanitized,
    }))
  }

  const resetHours = () => {
    setHoursValues(DEFAULT_HOURS)
  }

  const {
    isBirthdayEnabled,
    hasBirthdayBalance,
    isBirthdayTaken: isBirthdayTakenFlag,
  } = useMemo(
    () => ({
      isBirthdayEnabled: isBirthdayAvailable(timeOffBalanceData),
      hasBirthdayBalance: hasBirthdayHours(timeOffBalanceData),
      isBirthdayTaken: isBirthdayUnavailable(timeOffBalanceData),
    }),
    [timeOffBalanceData]
  )

  return {
    timeOffBalanceData,
    resultingBalances,
    deductedBalances,
    hoursValues,
    setHoursValues,
    updateHours,
    resetHours,
    isLoading,
    hasError,
    isBirthdayEnabled,
    hasBirthdayBalance,
    isBirthdayTaken: isBirthdayTakenFlag,
  }
}

export type { UseTimeOffBalanceReturn }
